import { createContext, useContext, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Alert, Box, Button } from '@mui/material';
import { SplashScreen } from '@/components/SplashScreen';
import { useAsync } from '@/hooks/useAsync';
import { buscaEu } from './service';
import type { Eu } from './types';

interface EuValor {
  eu: Eu;
  recarrega: () => void;
}

const Ctx = createContext<EuValor | null>(null);

export function useEu() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useEu fora do <EuProvider>');
  return v;
}

/**
 * Carrega /api/eu uma vez (depois do login) e decide para onde o usuário vai:
 * sem RA confirmado → /vincular; professor sem RA → painel do professor.
 */
export function EuProvider({ children }: { children: ReactNode }) {
  const { data, error, loading, reload } = useAsync((signal) => buscaEu(signal), []);
  const { pathname } = useLocation();
  if (loading && !data) return <SplashScreen />;
  if (error || !data) {
    return (
      <Box sx={{ p: 4, maxWidth: 520, mx: 'auto' }}>
        <Alert severity="error" action={<Button onClick={reload}>Tentar de novo</Button>}>
          Não consegui falar com o servidor agora.
        </Alert>
      </Box>
    );
  }
  const livre =
    pathname.startsWith('/vincular') ||
    pathname.startsWith('/como-funciona') ||
    pathname.startsWith('/admin');
  if (!data.vinculo && !livre) return <Navigate to={data.admin ? '/admin' : '/vincular'} replace />;
  if (data.vinculo && pathname.startsWith('/vincular')) return <Navigate to="/" replace />;
  if (!data.admin && pathname.startsWith('/admin')) return <Navigate to="/" replace />;
  return <Ctx.Provider value={{ eu: data, recarrega: reload }}>{children}</Ctx.Provider>;
}
